import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { User } from "@nelurio/contracts";
import { api, ApiClientError, clearCsrf, isDemoMode } from "../api/client";
import { demoUser } from "../demo/data";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (displayName: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(isDemoMode() ? demoUser : null);
  const [loading, setLoading] = useState(!isDemoMode());

  const refresh = async (): Promise<void> => {
    if (isDemoMode()) {
      setUser(demoUser);
      setLoading(false);
      return;
    }
    // Only a definitive 401/403 ends the session. Network hiccups — API restart
    // windows, flaky/mobile connections — retry instead of throwing the user to
    // the login page (that bounce looked like an infinite login loop).
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        setUser(await api<User>("/auth/me"));
        setLoading(false);
        return;
      } catch (reason) {
        if (reason instanceof ApiClientError && (reason.status === 401 || reason.status === 403)) {
          setUser(null);
          setLoading(false);
          return;
        }
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
      }
    }
    // Sustained outage: keep whatever session state we had and stop blocking boot.
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  const login = async (email: string, password: string) => {
    if (isDemoMode()) {
      setUser({ ...demoUser, email });
      return;
    }
    const result = await api<{ user: User }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password })
    });
    setUser(result.user);
  };

  const register = async (displayName: string, email: string, password: string) => {
    if (isDemoMode()) {
      setUser({ ...demoUser, displayName, email });
      return;
    }
    const result = await api<{ user: User }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ displayName, email, password })
    });
    setUser(result.user);
  };

  const logout = async () => {
    if (!isDemoMode()) {
      await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    }
    clearCsrf();
    setUser(null);
  };

  const value = useMemo(() => ({ user, loading, login, register, logout, refresh }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
